import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listQualificationCandidates } from '@/services/api'
import { Button } from '@/components/ui/button'

interface CandidateRow {
  id: string
  institution_name: string
  contact_name: string
  contact_email: string
  cbs_software: string
  qualification_interest: string
  category: string
  created_at: string
}

const CATEGORY_STYLES: Record<string, string> = {
  green: 'bg-green-100 text-green-800 border-green-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  red: 'bg-red-100 text-red-800 border-red-300',
}

const CATEGORY_LABELS: Record<string, string> = {
  green: 'Vert',
  orange: 'Orange',
  yellow: 'Jaune',
  red: 'Rouge',
}

export default function QualificationCandidatesList() {
  const [category, setCategory] = useState<string | undefined>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['qualification-candidates', category],
    queryFn: () => listQualificationCandidates({ category, limit: 100 }),
    retry: 2,
  })

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-bronze shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Candidats programme de qualification</h1>
            <p className="text-white/70 text-sm">Soumissions de pi-integration.io/programme-qualification</p>
          </div>
          <Link to="/ops-portal">
            <Button variant="outline" className="border-white text-white bg-transparent hover:bg-white/20 hover:text-white">
              Retour au dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Filtrer par catégorie</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={category === undefined ? 'default' : 'outline'}
              onClick={() => setCategory(undefined)}
              className={category === undefined ? 'bg-gold hover:bg-gold/90' : 'border-border'}
            >
              Tous
            </Button>
            {(['green', 'orange', 'yellow', 'red'] as const).map((c) => (
              <Button
                key={c}
                variant={category === c ? 'default' : 'outline'}
                onClick={() => setCategory(c)}
                className={category === c ? 'bg-gold hover:bg-gold/90' : 'border-border'}
              >
                {CATEGORY_LABELS[c]}
              </Button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden">
          {isLoading && <div className="p-6 text-muted-foreground">Chargement...</div>}
          {isError && <div className="p-6 text-destructive">Erreur lors du chargement</div>}
          {data?.items && data.items.length === 0 && (
            <div className="p-6 text-muted-foreground">Aucun candidat</div>
          )}
          {data?.items && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Catégorie</th>
                  <th className="text-left p-3">Institution</th>
                  <th className="text-left p-3">Contact</th>
                  <th className="text-left p-3">CBS</th>
                  <th className="text-left p-3">Intérêt</th>
                  <th className="text-left p-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row: CandidateRow) => (
                  <tr key={row.id} className="border-t border-border hover:bg-muted/50">
                    <td className="p-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold border ${
                          CATEGORY_STYLES[row.category] || 'bg-gray-100 text-gray-800 border-gray-300'
                        }`}
                      >
                        {CATEGORY_LABELS[row.category] || row.category}
                      </span>
                    </td>
                    <td className="p-3 font-medium">
                      <Link to={`/ops-portal/qualification-candidates/${row.id}`} className="text-bronze hover:text-gold">
                        {row.institution_name}
                      </Link>
                    </td>
                    <td className="p-3">
                      <div>{row.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{row.contact_email}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">{row.cbs_software}</td>
                    <td className="p-3 text-muted-foreground">{row.qualification_interest}</td>
                    <td className="p-3 text-muted-foreground">
                      {new Date(row.created_at).toLocaleString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && (
          <div className="mt-4 text-sm text-muted-foreground">
            {data.total} candidat{data.total !== 1 ? 's' : ''} au total
          </div>
        )}
      </main>
    </div>
  )
}
